/**
 * UTF-8-safe base64 encode: converts a string to UTF-8 bytes, then to base64.
 * This avoids `btoa`'s Latin1-only restriction for non-ASCII characters.
 */
function utf8ToBase64(str: string): string {
    const bytes = new TextEncoder().encode(str);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

/**
 * UTF-8-safe base64 decode: converts base64 to a Latin1 byte string, then
 * interprets those bytes as UTF-8 to recover the original string.
 */
function base64ToUtf8(base64: string): string {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
}

/**
 * Encode filters into a URL-safe base64 string, stripping default values.
 * Returns `undefined` if all values match defaults.
 */
export function encodeFilters<T>(filters: T, defaults: T): string | undefined {
    const filtersRecord = filters as Record<string, unknown>;
    const defaultsRecord = defaults as Record<string, unknown>;
    const nonDefaults: Record<string, unknown> = {};
    let hasNonDefault = false;

    for (const key of Object.keys(defaultsRecord)) {
        if (filtersRecord[key] !== defaultsRecord[key]) {
            nonDefaults[key] = filtersRecord[key];
            hasNonDefault = true;
        }
    }

    if (!hasNonDefault) {
        return undefined;
    }

    const json = JSON.stringify(nonDefaults);
    const base64 = utf8ToBase64(json);

    // Make URL-safe: + → -, / → _, strip trailing =
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Decode a URL-safe base64 string back into a filters object.
 * Falls back to defaults on any parse error.
 */
export function decodeFilters<T>(encoded: string | undefined, defaults: T): T {
    if (!encoded) {
        return { ...defaults };
    }

    try {
        // Restore standard base64: - → +, _ → /, re-pad with =
        let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
        const padLength = (4 - (base64.length % 4)) % 4;
        base64 += '='.repeat(padLength);

        const json = base64ToUtf8(base64);
        const parsed = JSON.parse(json) as Record<string, unknown>;
        const defaultsRecord = defaults as Record<string, unknown>;

        // Merge parsed values over defaults, only for keys that exist in defaults
        const result = { ...defaults };
        const resultRecord = result as Record<string, unknown>;
        for (const key of Object.keys(defaultsRecord)) {
            if (key in parsed && typeof parsed[key] === typeof defaultsRecord[key]) {
                resultRecord[key] = parsed[key];
            }
        }

        return result;
    } catch {
        // Malformed or stale encoded value — fall back to defaults
        return { ...defaults };
    }
}

const LOOP_PATHNAMES = new Set(['/auth', '/coming-soon']);

const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

// Matching control chars is intentional here (we reject them), so allow the regex.
// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_PATTERN = /[\x00-\x1f]/;

/**
 * Returns `raw` only if it is a safe, same-origin relative path (optionally
 * with query/hash) that doesn't loop back into the auth flow; otherwise
 * `undefined`. This is the open-redirect guard: every post-login redirect
 * target must pass through it.
 */
export function sanitizeRedirect(
    raw: string | undefined | null,
): string | undefined {
    if (!raw) return undefined;
    if (CONTROL_CHAR_PATTERN.test(raw) || /\s/.test(raw)) return undefined;
    if (!raw.startsWith('/')) return undefined;

    // Reject protocol-relative ("//evil.com") and backslash ("/\evil.com") tricks.
    if (raw.startsWith('//') || raw.startsWith('/\\')) return undefined;
    if (raw.includes('://') || raw.includes('\\')) return undefined;

    let decoded: string;
    try {
        decoded = decodeURIComponent(raw);
    } catch {
        return undefined;
    }

    // Re-check after decoding to catch encoded variants of the same tricks.
    if (CONTROL_CHAR_PATTERN.test(decoded)) return undefined;
    if (decoded.startsWith('//') || decoded.startsWith('/\\')) return undefined;
    if (decoded.includes('://') || decoded.includes('\\')) return undefined;

    const decodedPathname = decoded.split(/[?#]/)[0];
    if (SCHEME_PATTERN.test(decodedPathname)) return undefined;

    // Match on the decoded pathname so encoded variants (e.g. "/%61uth") can't slip past.
    if (LOOP_PATHNAMES.has(decodedPathname)) return undefined;

    return raw;
}

/**
 * Parses a `page` URL search value. 1-based, so anything below 1 is as
 * meaningless as a word. Shared by every `usePagination` call site that
 * persists its page in the URL.
 */
export function parseOptionalPage(value: unknown): number | undefined {
    if (value === undefined || value === null || value === '') return undefined;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
}
